import {
    PatientDTO,
    SeriesDTO,
    StudyDTO,
} from '~/backend/connector-api/connector-schemas';
import { Modality } from '~/domain/dicom/modality';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { Patient } from '~/domain/patient/patient';
import { Plan } from '~/domain/patient/plan';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { StructureSet } from '~/domain/patient/structureset';
import { cutUID } from '~/utils/string';

export type SeriesDataObject =
    | Image3D
    | StructureSet
    | DoseMatrix
    | RigidRegistration
    | SeriesDTO
    | Plan;

export type SeriesData = {
    id: string;
    modality: Modality;
    description?: string;
    seriesNumber?: number;
    length?: number;
    slice?: SliceData;
    study?: StudyDTO;
    seriesDate?: Date;
    seriesDataObject: SeriesDataObject;
};

export type SliceData = {
    rows: number;
    cols: number;
    sliceThickness: number;
};

export type PatientNode = {
    id: string;
    name: string;
    series: SeriesData[];
};

export const transformCTImageData = (images: Image3D[]): SeriesData[] => {
    return images.map((image: Image3D) => {
        const { modality, seriesNumber, seriesDescription, seriesDate } =
            image.seriesMetadata;

        return {
            id: image.seriesUid,
            modality: modality ?? Modality.UNKNOWN,
            description: seriesDescription,
            seriesNumber,
            length: image.length,
            slice: image.firstSlice,
            seriesDate,
            seriesDataObject: image,
        };
    });
};

export const transformStructureSetData = (
    structures: StructureSet[]
): SeriesData[] => {
    return structures.map((structureSet: StructureSet) => {
        return {
            id: structureSet.dicomStructureSet.sopInstanceUID,
            modality:
                structureSet.dicomStructureSet.modality ?? Modality.RTSTRUCT,
            description: structureSet.structureSetLabel,
            seriesDataObject: structureSet,
        };
    });
};

export const transformDoseMatrixData = (doses: DoseMatrix[]): SeriesData[] => {
    return doses
        .filter(
            (item, index, self) =>
                index === self.findIndex((t) => t.dicomUid === item.dicomUid)
        )
        .map((dosage: DoseMatrix) => {
            const label = `${dosage.dicomUid} (${cutUID(dosage.dicomUid)})`;
            return {
                id: dosage.dicomUid,
                modality: dosage.dicomDose?.modality ?? Modality.RTDOSE,
                description: dosage.plan?.label,
                label,
                seriesDataObject: dosage,
            };
        });
};

export const transformDosePlanData = (plans: Plan[]): SeriesData[] => {
    return plans
        .filter(
            (item, index, self) =>
                index ===
                self.findIndex((t) => t.instanceUid === item.instanceUid)
        )
        .map((plan: Plan) => {
            const label = `${plan.label}`;
            return {
                id: plan.instanceUid,
                modality: Modality.RTPLAN,
                description: plan.label,
                label,
                seriesDataObject: plan,
            };
        });
};

export const transformRegistrationData = (
    registration: RigidRegistration[]
): SeriesData[] => {
    return registration.map((registration: RigidRegistration) => {
        const label = `${cutUID(
            registration.sourceFrameOfReferenceUid
        )} to ${cutUID(registration.targetFrameOfReferenceUid)}`;
        return {
            id: registration.dicomParent.sopInstanceUID,
            modality: registration.dicomParent.modality ?? Modality.REG,
            label: 'REG',
            description: label,
            seriesDataObject: registration,
        };
    });
};

export const transformPatientData = (patient: Patient): PatientNode => {
    const ctImages = transformCTImageData(Array.from(patient.images));
    const structureSets = transformStructureSetData(
        Array.from(patient.structureSets)
    );
    const dosageData = transformDoseMatrixData(Array.from(patient.doses));
    const registrationData = transformRegistrationData(
        Array.from(patient.registrations)
    );

    const patientNode = {
        id: patient.dataSet.PatientID,
        name: patient.dataSet.PatientName,
        series: [
            ...ctImages,
            ...structureSets,
            ...dosageData,
            ...registrationData,
        ],
    };
    return patientNode;
};

export const transformAvailablePatientData = (
    patient: PatientDTO
): PatientNode => {
    const seriesList: SeriesData[] = [];
    patient.studies?.forEach((study) => {
        study.series?.forEach((series) => {
            const transformedSeries: SeriesData = {
                id: series.seriesInstanceUID,
                modality: series.modality as Modality,
                description: series.seriesDescription ?? '',
                seriesNumber: series.seriesNumber
                    ? parseInt(series.seriesNumber)
                    : undefined,
                slice: undefined,
                study,
                seriesDataObject: series,
            };
            seriesList.push(transformedSeries);
        });
    });

    const patientNode: PatientNode = {
        id: patient.patientId ?? '',
        name: patient.patientName ?? '',
        series: seriesList,
    };

    return patientNode;
};
